import { useState, useCallback } from 'react';
import { UploadedFile } from '../api/models';
import { getKnowledgeFileType, knowledgeRejectionMessage } from '../constants/fileUpload';

interface UseFileUploadProps {
    fileLimit?: number;
    documentSizeLimitMb?: number;
    videoSizeLimitMb?: number;
    onError?: (message: string) => void;
}

export const useFileUpload = ({
    fileLimit = 500,
    documentSizeLimitMb = 20,
    videoSizeLimitMb = 2048,
    onError
}: UseFileUploadProps = {}) => {
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);

    const documentSizeLimitBytes = documentSizeLimitMb * 1024 * 1024;
    const videoSizeLimitBytes = videoSizeLimitMb * 1024 * 1024;

    const addFiles = useCallback((files: File[]) => {
        setUploadedFiles((prevFiles) => {
            const existingFileNames = prevFiles.map((uf) => uf.file.name);
            const newFiles: UploadedFile[] = [];

            for (const file of files) {
                const fileType = getKnowledgeFileType(file.name);

                // Validate file type
                if (!fileType) {
                    onError?.(knowledgeRejectionMessage(file.name));
                    continue;
                }

                // Validate file size based on type
                const sizeLimit = fileType === 'document' ? documentSizeLimitBytes : videoSizeLimitBytes;
                const sizeLimitDisplay = fileType === 'document'
                    ? `${documentSizeLimitMb}MB`
                    : `${videoSizeLimitMb / 1024}GB`;

                if (file.size > sizeLimit) {
                    onError?.(`File "${file.name}" exceeds ${sizeLimitDisplay} file size limit`);
                    continue;
                }

                // Check for duplicates
                if (existingFileNames.includes(file.name)) {
                    continue;
                }

                newFiles.push({ file, type: fileType });
            }

            const totalFiles = prevFiles.length + newFiles.length;
            if (totalFiles > fileLimit) {
                onError?.(`You can only upload up to ${fileLimit} files.`);
                return prevFiles;
            }

            return [...prevFiles, ...newFiles];
        });
    }, [fileLimit, documentSizeLimitBytes, videoSizeLimitBytes, documentSizeLimitMb, videoSizeLimitMb, onError]);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
        const files = Array.from(event.dataTransfer.files);
        addFiles(files);
    }, [addFiles]);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        addFiles(files);
    }, [addFiles]);

    const handleRemoveFile = useCallback((index: number) => {
        setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    }, []);

    const resetFiles = useCallback(() => {
        setUploadedFiles([]);
    }, []);

    return {
        uploadedFiles,
        isDragOver,
        handleDrop,
        handleDragOver,
        handleDragLeave,
        handleFileChange,
        handleRemoveFile,
        resetFiles,
        documentSizeLimitMb,
        videoSizeLimitGb: videoSizeLimitMb / 1024,
        fileLimit
    };
};
